import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import GridLayout, { WidthProvider, type Layout } from 'react-grid-layout';
import type { WidgetStackNavStyle, WidgetGroupDirection } from './types';
import WidgetStackContainer from './WidgetStackContainer';
import WidgetGroupContainer from './WidgetGroupContainer';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AutoFixHigh as AutoLayoutIcon,
  ContentCopy as CopyIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditMapIcon,
  GroupWork as GroupIcon,
  Layers as LayersIcon,
  OpenInFull as ExpandTextIcon,
  Palette as PaletteIcon,
  RestartAlt as ResetIcon,
  Settings as SettingsIcon,
  Tune as TuneIcon,
  ViewSidebar as SidePanelIcon,
} from '@mui/icons-material';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './dashboardBuilderGrid.css';

import type {
  DashboardBannerStrip,
  DashboardBuilderState,
  DashboardDateFilterTargetRow,
  DashboardHeaderChrome,
  DashboardHeaderWidget,
  DashboardWidgetInstance,
  DashboardWidgetType,
} from './types';
import { defaultDashboardBuilderState } from './defaultState';
import { DEFAULT_DASHBOARD_HEADER_CHROME, DEFAULT_HEADER_WIDGETS, mergeDashboardHeaderDefaults } from './dashboardHeaderDefaults';
import { syncFilterModesFromHeaderWidgets } from './headerWidgetFilterSync';
import {
  fetchDashboard,
  apiUpdateDashboard,
} from '@signals/app-shell/features/userContent/contentLibraryStorage';
import type { SavedDashboard } from '@signals/app-shell/features/userContent/types';
import DashboardHeaderBar from './DashboardHeaderBar';
import DashboardHeaderSettingsDialog from './DashboardHeaderSettingsDialog';
import HeaderWidgetSettingsDialog from './HeaderWidgetSettingsDialog';
import WidgetSettingsDialog from './WidgetSettingsDialog';
import TextWidgetFullscreenEditor from './TextWidgetFullscreenEditor';
import { cardStyleFromWidget, contentStyleFromWidget, titleStyleFromWidget } from './dashboardStyleUtils';
import { bodyLooksLikeHtml, normalizeWidgetHtmlBody } from './richTextBody';
import { VizTabsWidget, DashboardSingleViz, GaugeWidget } from './VisualizationWidgets';
import { DEFAULT_VIZ_TABS } from './visualizationDefaults';
import ScaledWidgetContent from './ScaledWidgetContent';
import { chartDisplayFromConfig } from './chartDisplayUtils';
import IndicatorWidget from './IndicatorWidget';
import DataTableWidget from './DataTableWidget';
import DashboardThemeDialog from './DashboardThemeDialog';
import { DashboardThemeProvider } from './DashboardThemeContext';
import type { DashboardTheme } from './types';
import { useSettingsStore } from '@signals/app-shell/stores/settingsStore';
import WebMapWidget from '../webMap/WebMapWidget';
import DashboardSidePanel from './SidePanelWidget';
import DashboardSettingsDialog from './DashboardSettingsDialog';
import type { DashboardSidePanelConfig, DashboardLayoutMode } from './types';
import { WebMapProvider, useWebMapProvider } from '../webMap/WebMapContext';
import SpatialAnalyticsWidget from '../spatialAnalytics/SpatialAnalyticsWidget';
import GeofenceWidget from '../webMap/GeofenceWidget';
import DeviceListWidget from './DeviceListWidget';
import ActivityTimelineWidget from './ActivityTimelineWidget';
import LiveStatusWidget from './LiveStatusWidget';
import { autoLayout, suggestWidgets, applySuggestions, type WidgetSuggestion } from './autoLayoutEngine';

const GridWithWidth = WidthProvider(GridLayout);

const DRAG_HANDLE_CLASS = 'dashboard-widget-drag-handle';

const WIDGET_DEFAULT_TITLES: Record<DashboardWidgetType, string> = {
  placeholder: 'Placeholder',
  iframe: 'Embed',
  text: 'Text',
  viz_tabs: 'Charts (tabs)',
  chart_bar: 'Bar chart',
  chart_line: 'Line chart',
  chart_area: 'Area chart',
  chart_donut: 'Donut chart',
  chart_radar: 'Radar chart',
  chart_scatter: 'Scatter plot',
  chart_treemap: 'Treemap',
  calendar_heatmap: 'Calendar heatmap',
  gauge: 'Gauge',
  indicator: 'Indicator',
  data_table: 'Data table',
  device_list: 'Device List',
  activity_timeline: 'Activity Timeline',
  live_status: 'Live Status',
  web_map: 'Web Map',
  spatial_analytics: 'Smart Analytics',
  geofence: 'Geofences',
  widget_stack: 'Widget Stack',
  widget_group: 'Widget Group',
};

/** Add-widget menu section labels */
const addWidgetMenuCategorySx = {
  lineHeight: 1.75,
  py: 0.75,
  mt: 0.5,
  color: 'primary.main',
  fontWeight: 700,
  fontSize: '0.7rem',
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  borderBottom: 1,
  borderColor: 'primary.main',
  bgcolor: 'transparent',
};

function buildDateFilterTargets(
  gridLayout: Layout[],
  widgets: Record<string, DashboardWidgetInstance>
): DashboardDateFilterTargetRow[] {
  return gridLayout
    .map((item) => {
      const inst = widgets[item.i];
      if (!inst) return null;
      let label = WIDGET_DEFAULT_TITLES[inst.type] ?? item.i;
      const c = inst.config;
      if (c && 'chartTitle' in c && c.chartTitle?.trim()) label = c.chartTitle.trim();
      else if (inst.type === 'text' && c && 'title' in c && c.title?.trim()) label = c.title.trim();
      return {
        layoutId: item.i,
        label,
        perWidgetTimeSupported: false,
        widgetType: inst.type,
      };
    })
    .filter((x): x is DashboardDateFilterTargetRow => x != null);
}

let idCounter = 0;
function nextWidgetId(): string {
  idCounter += 1;
  return `w_${Date.now()}_${idCounter}`;
}

function layoutBottom(layout: Layout[]): number {
  return layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);
}

function defaultLayoutForType(type: DashboardWidgetType): Pick<Layout, 'w' | 'h' | 'minW' | 'minH'> {
  switch (type) {
    case 'viz_tabs':
      return { w: 8, h: 10, minW: 4, minH: 6 };
    case 'chart_bar':
    case 'chart_line':
    case 'chart_area':
    case 'chart_donut':
    case 'chart_radar':
    case 'chart_treemap':
      return { w: 5, h: 8, minW: 3, minH: 5 };
    case 'chart_scatter':
      return { w: 6, h: 8, minW: 3, minH: 5 };
    case 'calendar_heatmap':
      return { w: 6, h: 5, minW: 2, minH: 3 };
    case 'gauge':
      return { w: 3, h: 5, minW: 2, minH: 3 };
    case 'indicator':
      return { w: 3, h: 3, minW: 2, minH: 2 };
    case 'data_table':
      return { w: 8, h: 8, minW: 4, minH: 4 };
    case 'device_list':
      return { w: 4, h: 10, minW: 3, minH: 5 };
    case 'activity_timeline':
      return { w: 4, h: 10, minW: 3, minH: 5 };
    case 'live_status':
      return { w: 4, h: 8, minW: 3, minH: 4 };
    case 'iframe':
      return { w: 6, h: 8, minW: 3, minH: 4 };
    case 'web_map':
      return { w: 8, h: 10, minW: 4, minH: 6 };
    case 'spatial_analytics':
      return { w: 5, h: 10, minW: 3, minH: 6 };
    case 'geofence':
      return { w: 4, h: 8, minW: 3, minH: 5 };
    case 'widget_stack':
      return { w: 6, h: 8, minW: 3, minH: 4 };
    case 'widget_group':
      return { w: 8, h: 8, minW: 4, minH: 3 };
    default:
      return { w: 4, h: 4, minW: 2, minH: 2 };
  }
}

function newInstanceForType(type: DashboardWidgetType): DashboardWidgetInstance {
  if (type === 'iframe') {
    return { type: 'iframe', config: { iframeUrl: '' } };
  }
  if (type === 'text') {
    return {
      type: 'text',
      config: { title: 'New panel', body: 'Edit title and text in widget settings.' },
    };
  }
  if (type === 'viz_tabs') {
    return {
      type: 'viz_tabs',
      config: { tabs: DEFAULT_VIZ_TABS, heatmapWeeks: 6 },
    };
  }
  if (type === 'chart_bar') {
    return { type: 'chart_bar', config: {} };
  }
  if (type === 'chart_line') {
    return { type: 'chart_line' };
  }
  if (type === 'chart_area') {
    return { type: 'chart_area', config: {} };
  }
  if (type === 'chart_donut') {
    return { type: 'chart_donut', config: {} };
  }
  if (type === 'chart_radar') {
    return { type: 'chart_radar', config: {} };
  }
  if (type === 'chart_scatter') {
    return { type: 'chart_scatter', config: {} };
  }
  if (type === 'chart_treemap') {
    return { type: 'chart_treemap', config: {} };
  }
  if (type === 'calendar_heatmap') {
    return { type: 'calendar_heatmap', config: { heatmapWeeks: 6 } };
  }
  if (type === 'gauge') {
    return { type: 'gauge', config: { gaugeMin: 0, gaugeMax: 100, gaugeLabel: 'Value' } };
  }
  if (type === 'indicator') {
    return {
      type: 'indicator',
      config: {
        indicatorTextMiddle: 'Count: {value}',
        indicatorAccent: '#00e5ff',
        indicatorShowLastUpdated: false,
      },
    };
  }
  if (type === 'data_table') {
    return { type: 'data_table', config: { tablePageSize: 25 } };
  }
  if (type === 'device_list') {
    return { type: 'device_list', config: { showSearch: true, maxItems: 200, density: 'comfortable' } };
  }
  if (type === 'activity_timeline') {
    return { type: 'activity_timeline', config: { maxItems: 200 } };
  }
  if (type === 'live_status') {
    return { type: 'live_status', config: { activeThresholdMinutes: 30 } };
  }
  if (type === 'web_map') {
    return {
      type: 'web_map',
      config: {
        webMapOptions: {
          layers: [],
          basemap: 'streets',
        },
      },
    };
  }
  if (type === 'geofence') {
    return { type: 'geofence', config: { geofences: [], basemap: 'dark' } };
  }
  if (type === 'spatial_analytics') {
    return {
      type: 'spatial_analytics',
      config: {
        dataBinding: {},
        spatialInstructions: '',
      },
    };
  }
  if (type === 'widget_stack') {
    return {
      type: 'widget_stack',
      config: { childIds: [], activeChildIndex: 0, navStyle: 'tabs' },
    };
  }
  if (type === 'widget_group') {
    return {
      type: 'widget_group',
      config: { groupChildIds: [], direction: 'row' },
    };
  }
  return { type: 'placeholder' };
}

function widgetChromeTitle(instance: DashboardWidgetInstance): string {
  if (instance.config?.customTitle?.trim()) {
    return instance.config.customTitle.trim();
  }
  if (instance.type === 'text') {
    return instance.config?.title ?? WIDGET_DEFAULT_TITLES.text;
  }
  return WIDGET_DEFAULT_TITLES[instance.type];
}

/** Refresh minW/minH from current defaults so older saved dashboards can shrink compact widgets. */
function normalizeLayoutMinimums(state: DashboardBuilderState): DashboardBuilderState {
  const layout = state.layout.map((item) => {
    const inst = state.widgets[item.i];
    if (!inst) return item;
    const dims = defaultLayoutForType(inst.type);
    const minW = dims.minW ?? 1;
    const minH = dims.minH ?? 1;
    return {
      ...item,
      minW,
      minH,
      w: Math.max(item.w, minW),
      h: Math.max(item.h, minH),
    };
  });
  return { ...state, layout };
}

const DashboardBuilderPage: React.FC = () => {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { settings } = useSettingsStore();
  const hideHeaders = !!(settings.appearance.hideWidgetHeaders);
  const webMapContextValue = useWebMapProvider();
  const dashRef = useRef<SavedDashboard | null>(null);
  const [activeDashboard, setActiveDashboard] = useState<SavedDashboard | null>(null);
  const editMode = searchParams.get('edit') === 'true';
  const [state, setState] = useState<DashboardBuilderState>(() => defaultDashboardBuilderState());
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [addAnchor, setAddAnchor] = useState<null | HTMLElement>(null);
  const [autoLayoutAnchor, setAutoLayoutAnchor] = useState<null | HTMLElement>(null);
  const [suggestions, setSuggestions] = useState<WidgetSuggestion[]>([]);
  const [checkedSuggestions, setCheckedSuggestions] = useState<Set<DashboardWidgetType>>(new Set());
  const [settingsWidgetId, setSettingsWidgetId] = useState<string | null>(null);
  const [mapEditorWidgetId, setMapEditorWidgetId] = useState<string | null>(null);
  const [textEditorWidgetId, setTextEditorWidgetId] = useState<string | null>(null);
  const [headerSettingsOpen, setHeaderSettingsOpen] = useState(false);
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [headerWidgetSettingsId, setHeaderWidgetSettingsId] = useState<string | null>(null);

  // --- fit-to-screen dynamic row height ---
  const gridContainerRef = useRef<HTMLDivElement | null>(null);
  const [gridContainerHeight, setGridContainerHeight] = useState<number>(0);

  useEffect(() => {
    const el = gridContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setGridContainerHeight(entry.contentRect.height);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // --- drag-to-stack / group ---
  const [stackTargetId, setStackTargetId] = useState<string | null>(null);
  const [groupTargetId, setGroupTargetId] = useState<string | null>(null);
  const [groupTargetDir, setGroupTargetDir] = useState<WidgetGroupDirection>('row');
  const [groupTargetEdge, setGroupTargetEdge] = useState<'left' | 'right' | 'top' | 'bottom'>('right');
  /** True while Ctrl is held during an active drag — freezes other widgets in place. */
  const [ctrlDragging, setCtrlDragging] = useState(false);
  const preDragLayoutRef = useRef<Layout[] | null>(null);
  const draggedItemIdRef = useRef<string | null>(null);
  const ctrlHeldRef = useRef(false);

  useEffect(() => {
    if (!dashboardId) {
      navigate('/content', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      const d = await fetchDashboard(dashboardId);
      if (cancelled) return;
      if (!d) {
        navigate('/content', { replace: true });
        return;
      }

      // Strip legacy default widgets (placeholder + "Getting started" text)
      const wKeys = Object.keys(d.state.widgets);
      if (
        wKeys.length > 0 &&
        wKeys.length <= 2 &&
        wKeys.every((k) => {
          const w = d.state.widgets[k];
          if (!w) return false;
          const t = w.type as string;
          const cfg = w.config as Record<string, unknown> | undefined;
          return (
            t === 'placeholder' ||
            (t === 'iframe' && (!cfg || !cfg.url)) ||
            (t === 'text' && cfg?.title === 'Getting started')
          );
        })
      ) {
        d.state.layout = [];
        d.state.widgets = {};
        apiUpdateDashboard(dashboardId, { state: d.state }).catch(() => {});
      }

      dashRef.current = d;
      setActiveDashboard(d);
      setState(normalizeLayoutMinimums(mergeDashboardHeaderDefaults(d.state)));
    })();
    return () => { cancelled = true; };
  }, [dashboardId, navigate]);

  const settingsInstance = settingsWidgetId ? state.widgets[settingsWidgetId] : null;

  const layout = useMemo(() => state.layout as Layout[], [state.layout]);

  const scheduleSave = useCallback(
    (next: DashboardBuilderState) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        const d = dashRef.current;
        if (!dashboardId || !d) {
          saveTimer.current = null;
          return;
        }
        const updated: SavedDashboard = {
          ...d,
          state: next,
          updatedAt: new Date().toISOString(),
        };
        dashRef.current = updated;
        setActiveDashboard(updated);
        apiUpdateDashboard(dashboardId, { state: next }).catch(() => { /* offline fallback */ });
        saveTimer.current = null;
      }, 400);
    },
    [dashboardId]
  );

  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    []
  );

  const handleLayoutChange = useCallback(
    (nextLayout: Layout[]) => {
      setState((prev) => {
        const next = { ...prev, layout: nextLayout.map((l) => ({ ...l })) };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave]
  );

  const addWidget = useCallback(
    (type: DashboardWidgetType) => {
      const id = nextWidgetId();
      const y = layoutBottom(layout);
      const instance = newInstanceForType(type);
      const dim = defaultLayoutForType(type);

      setState((prev) => {
        const nextLayout: Layout[] = [
          ...prev.layout,
          { i: id, x: 0, y, ...dim },
        ];
        const next: DashboardBuilderState = {
          ...prev,
          layout: nextLayout,
          widgets: { ...prev.widgets, [id]: instance },
        };
        scheduleSave(next);
        return next;
      });
      setAddAnchor(null);
    },
    [layout, scheduleSave]
  );

  const removeWidget = useCallback(
    (id: string) => {
      setState((prev) => {
        const { [id]: _, ...rest } = prev.widgets;
        const next: DashboardBuilderState = {
          ...prev,
          layout: prev.layout.filter((l) => l.i !== id),
          widgets: rest,
        };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave]
  );

  const duplicateWidget = useCallback(
    (id: string) => {
      setState((prev) => {
        const source = prev.widgets[id];
        if (!source) return prev;
        const srcLayout = prev.layout.find((l) => l.i === id);
        const newId = nextWidgetId();
        const y = layoutBottom(prev.layout);
        const dim = srcLayout
          ? { w: srcLayout.w, h: srcLayout.h, minW: srcLayout.minW, minH: srcLayout.minH }
          : defaultLayoutForType(source.type);
        const clone: DashboardWidgetInstance = JSON.parse(JSON.stringify(source));
        const next: DashboardBuilderState = {
          ...prev,
          layout: [...prev.layout, { i: newId, x: 0, y, ...dim }],
          widgets: { ...prev.widgets, [newId]: clone },
        };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave]
  );

  const addChildWidget = useCallback(
    (type: DashboardWidgetType): string => {
      const id = nextWidgetId();
      const instance = newInstanceForType(type);
      setState((prev) => {
        const next: DashboardBuilderState = {
          ...prev,
          widgets: { ...prev.widgets, [id]: instance },
        };
        scheduleSave(next);
        return next;
      });
      return id;
    },
    [scheduleSave],
  );

  const removeChildWidget = useCallback(
    (id: string) => {
      setState((prev) => {
        const { [id]: _, ...rest } = prev.widgets;
        const next: DashboardBuilderState = { ...prev, widgets: rest };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave],
  );

  const sidePanelConfig = state.sidePanel;
  const layoutMode: DashboardLayoutMode = state.layoutMode ?? 'scroll';

  const GRID_MARGIN = 12;
  const gridRowHeight = useMemo(() => {
    if (layoutMode !== 'fit' || gridContainerHeight <= 0 || layout.length === 0) return 36;
    const maxRow = layout.reduce((m, item) => Math.max(m, item.y + item.h), 0);
    if (maxRow <= 0) return 36;
    const fitHeight = (gridContainerHeight - (maxRow - 1) * GRID_MARGIN) / maxRow;
    return Math.max(8, Math.floor(fitHeight));
  }, [layoutMode, gridContainerHeight, layout]);

  const saveDashboardSettings = useCallback(
    (settings: { sidePanel?: DashboardSidePanelConfig; layoutMode?: DashboardLayoutMode }) => {
      setState((prev) => {
        const next: DashboardBuilderState = {
          ...prev,
          sidePanel: settings.sidePanel ?? prev.sidePanel,
          layoutMode: settings.layoutMode ?? prev.layoutMode,
        };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave],
  );

  const updateSidePanel = useCallback(
    (patch: Partial<DashboardSidePanelConfig>) => {
      setState((prev) => {
        const sp: DashboardSidePanelConfig = prev.sidePanel ?? { enabled: true, childWidgetIds: [], anchor: 'right', panelWidth: 340 };
        const next: DashboardBuilderState = { ...prev, sidePanel: { ...sp, ...patch } };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave],
  );

  const resetDefault = useCallback(() => {
    const fresh = mergeDashboardHeaderDefaults({ ...defaultDashboardBuilderState() });
    setState(fresh);
    const d = dashRef.current;
    if (dashboardId && d) {
      const updated: SavedDashboard = { ...d, state: fresh, updatedAt: new Date().toISOString() };
      dashRef.current = updated;
      setActiveDashboard(updated);
      apiUpdateDashboard(dashboardId, { state: fresh }).catch(() => { /* offline */ });
    }
  }, [dashboardId]);

  const handleAutoLayoutOpen = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const s = suggestWidgets(state);
      setSuggestions(s);
      setCheckedSuggestions(new Set(s.map((sg) => sg.type)));
      setAutoLayoutAnchor(e.currentTarget);
    },
    [state],
  );

  const handleOptimizeLayout = useCallback(() => {
    setState((prev) => {
      const next = autoLayout(prev);
      scheduleSave(next);
      return next;
    });
    setAutoLayoutAnchor(null);
  }, [scheduleSave]);

  const handleApplySuggestions = useCallback(() => {
    const types = [...checkedSuggestions];
    if (types.length === 0) return;
    setState((prev) => {
      const next = applySuggestions(prev, types);
      scheduleSave(next);
      return next;
    });
    setAutoLayoutAnchor(null);
  }, [checkedSuggestions, scheduleSave]);

  // --- drag target detection (Ctrl-gated) ---
  type GroupEdge = 'left' | 'right' | 'top' | 'bottom';
  type DragTargetResult =
    | { kind: 'stack'; targetId: string }
    | { kind: 'group'; targetId: string; direction: WidgetGroupDirection; edge: GroupEdge }
    | null;

  const computeDragTarget = useCallback(
    (draggedId: string, newItem: Layout): DragTargetResult => {
      const snap = preDragLayoutRef.current;
      if (!snap) return null;
      for (const other of snap) {
        if (other.i === draggedId) continue;
        const inst = state.widgets[other.i];
        if (!inst) continue;
        const draggedInst = state.widgets[draggedId];
        if (draggedInst?.type === 'widget_stack' && inst.type === 'widget_stack') continue;
        if (draggedInst?.type === 'widget_group' && inst.type === 'widget_group') continue;

        const ox1 = other.x, oy1 = other.y, ox2 = other.x + other.w, oy2 = other.y + other.h;
        const nx1 = newItem.x, ny1 = newItem.y, nx2 = newItem.x + newItem.w, ny2 = newItem.y + newItem.h;
        const ix1 = Math.max(ox1, nx1), iy1 = Math.max(oy1, ny1);
        const ix2 = Math.min(ox2, nx2), iy2 = Math.min(oy2, ny2);
        if (ix1 >= ix2 || iy1 >= iy2) continue;
        const interArea = (ix2 - ix1) * (iy2 - iy1);
        const smallerArea = Math.min(other.w * other.h, newItem.w * newItem.h);
        if (interArea / smallerArea <= 0.5) continue;

        const cx = (nx1 + nx2) / 2;
        const cy = (ny1 + ny2) / 2;
        const relX = (cx - ox1) / (ox2 - ox1);
        const relY = (cy - oy1) / (oy2 - oy1);
        const inCenterX = relX > 0.3 && relX < 0.7;
        const inCenterY = relY > 0.3 && relY < 0.7;

        if (inCenterX && inCenterY) {
          return { kind: 'stack', targetId: other.i };
        }
        // Determine the closest edge for the group indicator
        if (relX <= 0.3) {
          return { kind: 'group', targetId: other.i, direction: 'row', edge: 'left' };
        }
        if (relX >= 0.7) {
          return { kind: 'group', targetId: other.i, direction: 'row', edge: 'right' };
        }
        if (relY <= 0.3) {
          return { kind: 'group', targetId: other.i, direction: 'column', edge: 'top' };
        }
        return { kind: 'group', targetId: other.i, direction: 'column', edge: 'bottom' };
      }
      return null;
    },
    [state.widgets],
  );

  const handleDragStart = useCallback(
    (currentLayout: Layout[], _oldItem: Layout, _newItem: Layout, _placeholder: Layout, _e: MouseEvent, _element: HTMLElement) => {
      preDragLayoutRef.current = currentLayout.map((l) => ({ ...l }));
      draggedItemIdRef.current = _oldItem.i;
      const isCtrl = _e.ctrlKey || _e.metaKey;
      ctrlHeldRef.current = isCtrl;
      setCtrlDragging(isCtrl);
      setStackTargetId(null);
      setGroupTargetId(null);
    },
    [],
  );

  const handleDrag = useCallback(
    (_layout: Layout[], _oldItem: Layout, newItem: Layout, _placeholder: Layout, e: MouseEvent) => {
      const draggedId = draggedItemIdRef.current;
      if (!draggedId) return;
      const isCtrl = e.ctrlKey || e.metaKey;
      ctrlHeldRef.current = isCtrl;
      setCtrlDragging((prev) => prev !== isCtrl ? isCtrl : prev);

      if (!isCtrl) {
        setStackTargetId(null);
        setGroupTargetId(null);
        return;
      }

      // When preventCollision is active, newItem is constrained to avoid overlaps
      // so it can never overlap a target. Compute a virtual position from the raw
      // mouse coordinates instead.
      let detectItem = newItem;
      const containerEl = gridContainerRef.current;
      const snap = preDragLayoutRef.current;
      if (containerEl && snap) {
        const rect = containerEl.getBoundingClientRect();
        const colWidth = (rect.width - GRID_MARGIN * 11) / 12;
        const cellW = colWidth + GRID_MARGIN;
        const cellH = gridRowHeight + GRID_MARGIN;
        const mouseGridX = (e.clientX - rect.left + containerEl.scrollLeft) / cellW;
        const mouseGridY = (e.clientY - rect.top + containerEl.scrollTop) / cellH;
        const draggedSnap = snap.find((l) => l.i === draggedId);
        if (draggedSnap) {
          detectItem = {
            ...draggedSnap,
            x: mouseGridX - draggedSnap.w / 2,
            y: mouseGridY - draggedSnap.h / 2,
          };
        }
      }

      const result = computeDragTarget(draggedId, detectItem);
      if (!result) {
        setStackTargetId(null);
        setGroupTargetId(null);
      } else if (result.kind === 'stack') {
        setStackTargetId(result.targetId);
        setGroupTargetId(null);
      } else {
        setStackTargetId(null);
        setGroupTargetId(result.targetId);
        setGroupTargetDir(result.direction);
        setGroupTargetEdge(result.edge);
      }
    },
    [computeDragTarget, gridRowHeight],
  );

  const mergeIntoStack = useCallback(
    (draggedId: string, targetId: string) => {
      setState((prev) => {
        const draggedInst = prev.widgets[draggedId];
        const targetInst = prev.widgets[targetId];
        if (!draggedInst || !targetInst) return prev;
        const nextWidgets = { ...prev.widgets };
        let nextLayout = prev.layout.filter((l) => l.i !== draggedId);

        if (targetInst.type === 'widget_stack') {
          const existingIds = targetInst.config?.childIds ?? [];
          nextWidgets[targetId] = {
            ...targetInst,
            config: {
              ...targetInst.config,
              childIds: [...existingIds, draggedId],
              activeChildIndex: existingIds.length,
            },
          };
        } else {
          const stackId = nextWidgetId();
          const targetLayout = nextLayout.find((l) => l.i === targetId);
          const stackLayout = targetLayout
            ? { ...targetLayout, i: stackId }
            : { i: stackId, x: 0, y: layoutBottom(nextLayout), w: 6, h: 8, minW: 3, minH: 4 };
          nextLayout = nextLayout.filter((l) => l.i !== targetId);
          nextLayout.push(stackLayout);
          nextWidgets[stackId] = {
            type: 'widget_stack',
            config: { childIds: [targetId, draggedId], activeChildIndex: 0, navStyle: 'tabs' as WidgetStackNavStyle },
          };
        }

        const next: DashboardBuilderState = { ...prev, layout: nextLayout, widgets: nextWidgets };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave],
  );

  const mergeIntoGroup = useCallback(
    (draggedId: string, targetId: string, direction: WidgetGroupDirection, edge: 'left' | 'right' | 'top' | 'bottom') => {
      const insertBefore = edge === 'left' || edge === 'top';
      setState((prev) => {
        const draggedInst = prev.widgets[draggedId];
        const targetInst = prev.widgets[targetId];
        if (!draggedInst || !targetInst) return prev;
        const nextWidgets = { ...prev.widgets };
        let nextLayout = prev.layout.filter((l) => l.i !== draggedId);

        if (targetInst.type === 'widget_group' && targetInst.config?.direction === direction) {
          const existing = targetInst.config?.groupChildIds ?? [];
          const updated = insertBefore ? [draggedId, ...existing] : [...existing, draggedId];
          nextWidgets[targetId] = {
            ...targetInst,
            config: { ...targetInst.config, groupChildIds: updated },
          };
        } else {
          const groupId = nextWidgetId();
          const targetLayout = nextLayout.find((l) => l.i === targetId);
          const draggedLayout = prev.layout.find((l) => l.i === draggedId);
          let gw: number, gh: number;
          if (direction === 'row') {
            gw = (targetLayout?.w ?? 6) + (draggedLayout?.w ?? 6);
            gh = Math.max(targetLayout?.h ?? 8, draggedLayout?.h ?? 8);
          } else {
            gw = Math.max(targetLayout?.w ?? 6, draggedLayout?.w ?? 6);
            gh = (targetLayout?.h ?? 8) + (draggedLayout?.h ?? 8);
          }
          const groupLayout = targetLayout
            ? { ...targetLayout, i: groupId, w: Math.min(12, gw), h: gh }
            : { i: groupId, x: 0, y: layoutBottom(nextLayout), w: Math.min(12, gw), h: gh, minW: 4, minH: 3 };
          nextLayout = nextLayout.filter((l) => l.i !== targetId);
          nextLayout.push(groupLayout);
          const children = insertBefore ? [draggedId, targetId] : [targetId, draggedId];
          nextWidgets[groupId] = {
            type: 'widget_group',
            config: { groupChildIds: children, direction },
          };
        }

        const next: DashboardBuilderState = { ...prev, layout: nextLayout, widgets: nextWidgets };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave],
  );

  const handleDragStop = useCallback(
    (currentLayout: Layout[], _oldItem: Layout, _newItem: Layout) => {
      const draggedId = draggedItemIdRef.current;
      const sTarget = stackTargetId;
      const gTarget = groupTargetId;
      const gDir = groupTargetDir;
      const gEdge = groupTargetEdge;
      const snap = preDragLayoutRef.current;
      const wasCtrl = ctrlHeldRef.current;
      preDragLayoutRef.current = null;
      draggedItemIdRef.current = null;
      ctrlHeldRef.current = false;
      setCtrlDragging(false);
      setStackTargetId(null);
      setGroupTargetId(null);

      if (draggedId && sTarget) {
        mergeIntoStack(draggedId, sTarget);
        return;
      }
      if (draggedId && gTarget) {
        mergeIntoGroup(draggedId, gTarget, gDir, gEdge);
        return;
      }
      // When Ctrl was held but no target matched, restore the pre-drag layout
      // so items snap back to original positions instead of the displaced layout.
      if (wasCtrl && snap) {
        handleLayoutChange(snap);
        return;
      }
      handleLayoutChange(currentLayout);
    },
    [stackTargetId, groupTargetId, groupTargetDir, groupTargetEdge, mergeIntoStack, mergeIntoGroup, handleLayoutChange],
  );

  const unstackWidget = useCallback(
    (stackId: string, childId: string) => {
      setState((prev) => {
        const stackInst = prev.widgets[stackId];
        if (!stackInst || stackInst.type !== 'widget_stack') return prev;
        const childIds = (stackInst.config?.childIds ?? []).filter((id) => id !== childId);
        const nextWidgets = { ...prev.widgets };
        const childInst = nextWidgets[childId];

        if (childIds.length <= 1) {
          const remainingId = childIds[0];
          const stackLayout = prev.layout.find((l) => l.i === stackId);
          let nextLayout = prev.layout.filter((l) => l.i !== stackId);
          if (remainingId) {
            const remainInst = nextWidgets[remainingId];
            if (stackLayout && remainInst) {
              nextLayout.push({ ...stackLayout, i: remainingId });
            }
            delete nextWidgets[stackId];
          } else {
            delete nextWidgets[stackId];
          }
          if (childInst) {
            const dim = defaultLayoutForType(childInst.type);
            nextLayout.push({ i: childId, x: 0, y: layoutBottom(nextLayout), ...dim });
          }
          const next: DashboardBuilderState = { ...prev, layout: nextLayout, widgets: nextWidgets };
          scheduleSave(next);
          return next;
        }

        const activeIdx = stackInst.config?.activeChildIndex ?? 0;
        nextWidgets[stackId] = {
          ...stackInst,
          config: {
            ...stackInst.config,
            childIds,
            activeChildIndex: Math.min(activeIdx, childIds.length - 1),
          },
        };

        if (childInst) {
          const dim = defaultLayoutForType(childInst.type);
          const y = layoutBottom(prev.layout);
          const nextLayout = [...prev.layout, { i: childId, x: 0, y, ...dim }];
          const next: DashboardBuilderState = { ...prev, layout: nextLayout, widgets: nextWidgets };
          scheduleSave(next);
          return next;
        }

        const next: DashboardBuilderState = { ...prev, widgets: nextWidgets };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave],
  );

  const ungroupWidget = useCallback(
    (groupId: string, childId: string) => {
      setState((prev) => {
        const groupInst = prev.widgets[groupId];
        if (!groupInst || groupInst.type !== 'widget_group') return prev;
        const childIds = (groupInst.config?.groupChildIds ?? []).filter((id) => id !== childId);
        const nextWidgets = { ...prev.widgets };
        const childInst = nextWidgets[childId];

        if (childIds.length <= 1) {
          const remainingId = childIds[0];
          const groupLayout = prev.layout.find((l) => l.i === groupId);
          let nextLayout = prev.layout.filter((l) => l.i !== groupId);
          if (remainingId) {
            const remainInst = nextWidgets[remainingId];
            if (groupLayout && remainInst) {
              nextLayout.push({ ...groupLayout, i: remainingId });
            }
            delete nextWidgets[groupId];
          } else {
            delete nextWidgets[groupId];
          }
          if (childInst) {
            const dim = defaultLayoutForType(childInst.type);
            nextLayout.push({ i: childId, x: 0, y: layoutBottom(nextLayout), ...dim });
          }
          const next: DashboardBuilderState = { ...prev, layout: nextLayout, widgets: nextWidgets };
          scheduleSave(next);
          return next;
        }

        nextWidgets[groupId] = {
          ...groupInst,
          config: { ...groupInst.config, groupChildIds: childIds },
        };

        if (childInst) {
          const dim = defaultLayoutForType(childInst.type);
          const y = layoutBottom(prev.layout);
          const nextLayout = [...prev.layout, { i: childId, x: 0, y, ...dim }];
          const next: DashboardBuilderState = { ...prev, layout: nextLayout, widgets: nextWidgets };
          scheduleSave(next);
          return next;
        }

        const next: DashboardBuilderState = { ...prev, widgets: nextWidgets };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave],
  );

  const updateStackActiveIndex = useCallback(
    (stackId: string, idx: number) => {
      setState((prev) => {
        const inst = prev.widgets[stackId];
        if (!inst || inst.type !== 'widget_stack') return prev;
        const next: DashboardBuilderState = {
          ...prev,
          widgets: {
            ...prev.widgets,
            [stackId]: { ...inst, config: { ...inst.config, activeChildIndex: idx } },
          },
        };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave],
  );

  const updateGroupSplitRatios = useCallback(
    (groupId: string, ratios: number[]) => {
      setState((prev) => {
        const inst = prev.widgets[groupId];
        if (!inst || inst.type !== 'widget_group') return prev;
        const next: DashboardBuilderState = {
          ...prev,
          widgets: {
            ...prev.widgets,
            [groupId]: { ...inst, config: { ...inst.config, splitRatios: ratios } },
          },
        };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave],
  );

  const openSettings = useCallback((widgetId: string) => {
    setSettingsWidgetId(widgetId);
  }, []);

  const headerChrome: DashboardHeaderChrome = {
    ...DEFAULT_DASHBOARD_HEADER_CHROME,
    ...state.header,
  };
  const headerWidgets: DashboardHeaderWidget[] =
    state.headerWidgets !== undefined ? state.headerWidgets : [...DEFAULT_HEADER_WIDGETS];

  const dateFilterTargetRows = useMemo(
    () => buildDateFilterTargets(layout, state.widgets),
    [layout, state.widgets]
  );

  useEffect(() => {
    syncFilterModesFromHeaderWidgets(
      state.headerWidgets !== undefined ? state.headerWidgets : DEFAULT_HEADER_WIDGETS,
      webMapContextValue,
    );
  }, [state.headerWidgets, webMapContextValue]);

  const addHeaderWidget = useCallback(
    (w: DashboardHeaderWidget) => {
      setState((prev) => {
        const list = prev.headerWidgets !== undefined ? prev.headerWidgets : [...DEFAULT_HEADER_WIDGETS];
        const next: DashboardBuilderState = { ...prev, headerWidgets: [...list, w] };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave]
  );

  const removeHeaderWidget = useCallback(
    (id: string) => {
      setState((prev) => {
        const list = prev.headerWidgets !== undefined ? prev.headerWidgets : [...DEFAULT_HEADER_WIDGETS];
        const next: DashboardBuilderState = { ...prev, headerWidgets: list.filter((x) => x.id !== id) };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave]
  );

  const replaceHeaderWidget = useCallback(
    (w: DashboardHeaderWidget) => {
      setState((prev) => {
        const list = prev.headerWidgets !== undefined ? prev.headerWidgets : [...DEFAULT_HEADER_WIDGETS];
        const next: DashboardBuilderState = {
          ...prev,
          headerWidgets: list.map((x) => (x.id === w.id ? w : x)),
        };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave]
  );

  const editingHeaderWidget =
    headerWidgetSettingsId == null
      ? null
      : headerWidgets.find((w) => w.id === headerWidgetSettingsId) ?? null;

  useEffect(() => {
    if (headerWidgetSettingsId != null && !editingHeaderWidget) {
      setHeaderWidgetSettingsId(null);
    }
  }, [headerWidgetSettingsId, editingHeaderWidget]);

  const saveHeaderChrome = useCallback(
    (chrome: DashboardHeaderChrome, banner: DashboardBannerStrip | undefined) => {
      setState((prev) => {
        const next: DashboardBuilderState = {
          ...prev,
          header: chrome,
          banner: banner?.text?.trim() ? banner : undefined,
        };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave]
  );

  const saveTheme = useCallback(
    (theme: DashboardTheme | undefined) => {
      setState((prev) => {
        const next: DashboardBuilderState = { ...prev, theme };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave]
  );

  /** Pass `widgetLayoutId` when saving from an in-widget editor (e.g. Web Map); otherwise uses the open settings dialog id. */
  const handleWidgetConfigSave = useCallback(
    (config: DashboardWidgetInstance['config'], widgetLayoutId?: string) => {
      const widgetId = widgetLayoutId ?? settingsWidgetId;
      if (!widgetId) return;
      setState((prev) => {
        const cur = prev.widgets[widgetId];
        if (!cur) return prev;
        const next: DashboardBuilderState = {
          ...prev,
          widgets: {
            ...prev.widgets,
            [widgetId]: { ...cur, config },
          },
        };
        scheduleSave(next);
        return next;
      });
    },
    [settingsWidgetId, scheduleSave]
  );

  const renderWidgetBody = useCallback(
    (instance: DashboardWidgetInstance, layoutItemId: string): React.ReactNode => {
    if (instance.type === 'placeholder') {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="body2">Empty placeholder — configure this widget from the settings menu.</Typography>
        </Box>
      );
    }
    if (instance.type === 'iframe') {
      const url = instance.config?.iframeUrl?.trim();
      if (!url) {
        return (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Set an embed URL in widget settings.
            </Typography>
          </Box>
        );
      }
      return (
        <Box sx={{ height: '100%', width: '100%', overflow: 'hidden' }}>
          <iframe
            src={url}
            title="Embedded content"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            allow={instance.config?.iframeAllow ?? ''}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        </Box>
      );
    }
    if (instance.type === 'text') {
      const title = instance.config?.title ?? 'Text';
      const rawBody =
        instance.config?.body ?? '<p>Open settings (gear) in edit mode to change this text.</p>';
      const cs = contentStyleFromWidget(instance.config?.style, state.theme);
      const body = normalizeWidgetHtmlBody(rawBody);
      const isHtml = bodyLooksLikeHtml(rawBody);
      return (
        <Stack spacing={1} sx={{ height: '100%', overflow: 'auto', ...cs }}>
          <Typography variant="subtitle2" color="text.secondary" sx={cs}>
            {title}
          </Typography>
          {isHtml ? (
            <Box
              sx={{
                ...cs,
                fontSize: 14,
                '& img': { maxWidth: '100%', height: 'auto' },
                '& a': { color: 'primary.main' },
                '& p': { margin: '0.4em 0' },
                '& h1,& h2,& h3': { mt: 1, mb: 0.5 },
              }}
              dangerouslySetInnerHTML={{ __html: body }}
            />
          ) : (
            <Typography variant="body2" sx={cs}>
              {body}
            </Typography>
          )}
        </Stack>
      );
    }
    if (instance.type === 'viz_tabs') {
      const c = instance.config;
      const tabs = c?.tabs?.length ? c.tabs : DEFAULT_VIZ_TABS;
      return (
        <VizTabsWidget
          tabs={tabs}
          heatmapWeeks={c?.heatmapWeeks ?? 6}
          chartDisplay={chartDisplayFromConfig(c)}
          dataBinding={c?.dataBinding}
        />
      );
    }
    if (instance.type === 'chart_bar') {
      const c = instance.config;
      return (
        <DashboardSingleViz
          viz="bar"
          chartTitle={c?.chartTitle}
          chartDisplay={chartDisplayFromConfig(c)}
          dataBinding={c?.dataBinding}
        />
      );
    }
    if (instance.type === 'chart_line') {
      const c = instance.config;
      return (
        <DashboardSingleViz
          viz="line"
          chartTitle={c?.chartTitle}
          chartDisplay={chartDisplayFromConfig(c)}
          dataBinding={c?.dataBinding}
        />
      );
    }
    if (instance.type === 'chart_area') {
      const c = instance.config;
      return (
        <DashboardSingleViz
          viz="area"
          chartTitle={c?.chartTitle}
          chartDisplay={chartDisplayFromConfig(c)}
          dataBinding={c?.dataBinding}
        />
      );
    }
    if (instance.type === 'chart_donut') {
      const c = instance.config;
      return (
        <DashboardSingleViz
          viz="donut"
          chartTitle={c?.chartTitle}
          chartDisplay={chartDisplayFromConfig(c)}
          dataBinding={c?.dataBinding}
        />
      );
    }
    if (instance.type === 'chart_radar') {
      const c = instance.config;
      return (
        <DashboardSingleViz
          viz="radar"
          chartTitle={c?.chartTitle}
          chartDisplay={chartDisplayFromConfig(c)}
          dataBinding={c?.dataBinding}
        />
      );
    }
    if (instance.type === 'chart_scatter') {
      const c = instance.config;
      return (
        <DashboardSingleViz
          viz="scatter"
          chartTitle={c?.chartTitle}
          chartDisplay={chartDisplayFromConfig(c)}
          dataBinding={c?.dataBinding}
          scatterXField={c?.scatterXField}
          scatterYField={c?.scatterYField}
        />
      );
    }
    if (instance.type === 'chart_treemap') {
      const c = instance.config;
      return (
        <DashboardSingleViz
          viz="treemap"
          chartTitle={c?.chartTitle}
          chartDisplay={chartDisplayFromConfig(c)}
          dataBinding={c?.dataBinding}
        />
      );
    }
    if (instance.type === 'calendar_heatmap') {
      const c = instance.config;
      return (
        <DashboardSingleViz
          viz="calendar"
          chartTitle={c?.chartTitle}
          heatmapWeeks={c?.heatmapWeeks ?? 6}
          chartDisplay={chartDisplayFromConfig(c)}
          dataBinding={c?.dataBinding}
        />
      );
    }
    if (instance.type === 'gauge') {
      const c = instance.config;
      return (
        <GaugeWidget
          dataBinding={c?.dataBinding}
          gaugeMin={c?.gaugeMin}
          gaugeMax={c?.gaugeMax}
          gaugeLabel={c?.gaugeLabel}
          gaugeColor={c?.gaugeColor}
          chartColorPaletteId={c?.chartColorPaletteId}
        />
      );
    }
    if (instance.type === 'indicator') {
      return <IndicatorWidget widgetConfig={instance.config} />;
    }
    if (instance.type === 'data_table') {
      const c = instance.config;
      return (
        <DataTableWidget
          dataBinding={c?.dataBinding}
          pageSize={c?.tablePageSize}
          compact={c?.tableCompact}
          visibleColumns={c?.tableVisibleColumns}
          columnLabels={c?.tableColumnLabels}
        />
      );
    }
    if (instance.type === 'device_list') {
      return <DeviceListWidget widgetConfig={instance.config} />;
    }
    if (instance.type === 'activity_timeline') {
      return <ActivityTimelineWidget widgetConfig={instance.config} />;
    }
    if (instance.type === 'live_status') {
      return <LiveStatusWidget widgetConfig={instance.config} />;
    }
    if (instance.type === 'web_map') {
      return (
        <WebMapWidget
          widgetConfig={instance.config}
          layoutId={layoutItemId}
          editMode={editMode}
          onUpdateConfig={(cfg) => handleWidgetConfigSave(cfg, layoutItemId)}
          editorOpen={mapEditorWidgetId === layoutItemId}
          onEditorOpenChange={(open) => setMapEditorWidgetId(open ? layoutItemId : null)}
        />
      );
    }
    if (instance.type === 'spatial_analytics') {
      return <SpatialAnalyticsWidget widgetConfig={instance.config} />;
    }
    if (instance.type === 'geofence') {
      return (
        <GeofenceWidget
          widgetConfig={instance.config}
          editMode={editMode}
          onUpdateConfig={(cfg) => handleWidgetConfigSave(cfg, layoutItemId)}
        />
      );
    }
    if (instance.type === 'widget_stack') {
      const cIds = instance.config?.childIds ?? [];
      return (
        <WidgetStackContainer
          childIds={cIds}
          widgets={state.widgets}
          activeIndex={instance.config?.activeChildIndex ?? 0}
          navStyle={instance.config?.navStyle ?? 'tabs'}
          editMode={editMode}
          theme={state.theme}
          tabLabels={instance.config?.tabLabels}
          onActiveChange={(idx) => updateStackActiveIndex(layoutItemId, idx)}
          onOpenChildSettings={(childId) => openSettings(childId)}
          onRemoveChild={(childId) => unstackWidget(layoutItemId, childId)}
          renderWidgetBody={renderWidgetBody}
        />
      );
    }
    if (instance.type === 'widget_group') {
      const gIds = instance.config?.groupChildIds ?? [];
      return (
        <WidgetGroupContainer
          childIds={gIds}
          widgets={state.widgets}
          direction={instance.config?.direction ?? 'row'}
          splitRatios={instance.config?.splitRatios}
          editMode={editMode}
          theme={state.theme}
          onOpenChildSettings={(childId) => openSettings(childId)}
          onRemoveChild={(childId) => ungroupWidget(layoutItemId, childId)}
          onSplitChange={(ratios) => updateGroupSplitRatios(layoutItemId, ratios)}
          renderWidgetBody={renderWidgetBody}
        />
      );
    }
    return null;
  },
  [editMode, handleWidgetConfigSave, state.theme, mapEditorWidgetId, state.widgets, updateStackActiveIndex, updateGroupSplitRatios, unstackWidget, ungroupWidget, openSettings]);

  return (
    <WebMapProvider value={webMapContextValue}>
    <DashboardThemeProvider value={state.theme}>
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, p: 2 }}>
      <Stack spacing={1} sx={{ mb: 1, flexShrink: 0 }}>
        {editMode && activeDashboard ? (
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Button size="small" component={RouterLink} to="/content">
              ← My dashboards
            </Button>
            <Typography variant="subtitle2" color="text.secondary">
              {activeDashboard.name}
            </Typography>
          </Stack>
        ) : null}
        <DashboardHeaderBar
          chrome={headerChrome}
          banner={state.banner}
          widgets={headerWidgets}
          editMode={editMode}
          onAddWidget={addHeaderWidget}
          onRemoveWidget={removeHeaderWidget}
          onConfigureWidget={(w) => setHeaderWidgetSettingsId(w.id)}
        />
        {editMode && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="flex-end"
          flexWrap="wrap"
          useFlexGap
        >
              <Button startIcon={<TuneIcon />} onClick={() => setHeaderSettingsOpen(true)}>
                Header appearance
              </Button>
              <Button startIcon={<PaletteIcon />} onClick={() => setThemeDialogOpen(true)}>
                Theme
              </Button>
              <Button startIcon={<AddIcon />} onClick={(e) => setAddAnchor(e.currentTarget)}>
                Add widget
              </Button>
              <Menu
                anchorEl={addAnchor}
                open={Boolean(addAnchor)}
                onClose={() => setAddAnchor(null)}
                MenuListProps={{ dense: true }}
              >
                <ListSubheader sx={{ ...addWidgetMenuCategorySx, mt: 0 }}>General</ListSubheader>
                <MenuItem onClick={() => addWidget('iframe')}>Embed (iframe)</MenuItem>
                <MenuItem onClick={() => addWidget('text')}>Text panel</MenuItem>
                <ListSubheader sx={addWidgetMenuCategorySx}>Maps</ListSubheader>
                <MenuItem onClick={() => addWidget('web_map')}>Web Map (user data)</MenuItem>
                <MenuItem onClick={() => addWidget('geofence')}>Geofences</MenuItem>
                <ListSubheader sx={addWidgetMenuCategorySx}>Analytics</ListSubheader>
                <MenuItem onClick={() => addWidget('spatial_analytics')}>Spatial analytics (AI)</MenuItem>
                <ListSubheader sx={addWidgetMenuCategorySx}>Charts</ListSubheader>
                <MenuItem onClick={() => addWidget('viz_tabs')}>Tabbed charts</MenuItem>
                <MenuItem onClick={() => addWidget('chart_bar')}>Bar chart</MenuItem>
                <MenuItem onClick={() => addWidget('chart_line')}>Line chart</MenuItem>
                <MenuItem onClick={() => addWidget('chart_area')}>Area chart</MenuItem>
                <MenuItem onClick={() => addWidget('chart_donut')}>Donut / pie chart</MenuItem>
                <MenuItem onClick={() => addWidget('chart_radar')}>Radar chart</MenuItem>
                <MenuItem onClick={() => addWidget('chart_scatter')}>Scatter plot</MenuItem>
                <MenuItem onClick={() => addWidget('chart_treemap')}>Treemap</MenuItem>
                <MenuItem onClick={() => addWidget('calendar_heatmap')}>Calendar heatmap</MenuItem>
                <ListSubheader sx={addWidgetMenuCategorySx}>Metrics</ListSubheader>
                <MenuItem onClick={() => addWidget('gauge')}>Gauge</MenuItem>
                <MenuItem onClick={() => addWidget('indicator')}>Indicator (statistic)</MenuItem>
                <ListSubheader sx={addWidgetMenuCategorySx}>Data</ListSubheader>
                <MenuItem onClick={() => addWidget('data_table')}>Data table</MenuItem>
                <MenuItem onClick={() => addWidget('device_list')}>Device / entity list</MenuItem>
                <MenuItem onClick={() => addWidget('activity_timeline')}>Activity timeline</MenuItem>
                <MenuItem onClick={() => addWidget('live_status')}>Live status</MenuItem>
                <ListSubheader sx={addWidgetMenuCategorySx}>Layout</ListSubheader>
                <MenuItem onClick={() => addWidget('widget_stack')}>Widget Stack (layers)</MenuItem>
                <MenuItem onClick={() => addWidget('widget_group')}>Widget Group (side-by-side)</MenuItem>
              </Menu>
              <Button startIcon={<SidePanelIcon />} onClick={() => setSettingsDialogOpen(true)}>
                Settings
              </Button>
              <Button startIcon={<AutoLayoutIcon />} onClick={handleAutoLayoutOpen}>
                Auto-layout
              </Button>
              <Popover
                open={Boolean(autoLayoutAnchor)}
                anchorEl={autoLayoutAnchor}
                onClose={() => setAutoLayoutAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <Box sx={{ p: 2, width: 320 }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Auto-layout
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    startIcon={<AutoLayoutIcon />}
                    onClick={handleOptimizeLayout}
                    sx={{ mb: 1.5 }}
                  >
                    Optimize current layout
                  </Button>
                  {suggestions.length > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Suggested widgets for your dashboard:
                      </Typography>
                      {suggestions.map((s) => (
                        <FormControlLabel
                          key={s.type}
                          sx={{ display: 'flex', mr: 0, mb: 0.5 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={checkedSuggestions.has(s.type)}
                              onChange={(_, checked) =>
                                setCheckedSuggestions((prev) => {
                                  const next = new Set(prev);
                                  if (checked) next.add(s.type);
                                  else next.delete(s.type);
                                  return next;
                                })
                              }
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">{s.type.replace(/_/g, ' ')}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {s.reason}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                      <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        disabled={checkedSuggestions.size === 0}
                        onClick={handleApplySuggestions}
                        sx={{ mt: 1 }}
                      >
                        Add &amp; optimize
                      </Button>
                    </>
                  )}
                </Box>
              </Popover>
              <Button startIcon={<ResetIcon />} color="warning" onClick={resetDefault}>
                Reset layout
              </Button>
        </Stack>
        )}
      </Stack>

      <DashboardHeaderSettingsDialog
        open={headerSettingsOpen}
        initialChrome={headerChrome}
        initialBanner={state.banner}
        onClose={() => setHeaderSettingsOpen(false)}
        onSave={saveHeaderChrome}
      />

      <DashboardThemeDialog
        open={themeDialogOpen}
        initial={state.theme}
        onClose={() => setThemeDialogOpen(false)}
        onSave={saveTheme}
      />

      <DashboardSettingsDialog
        open={settingsDialogOpen}
        sidePanel={sidePanelConfig}
        layoutMode={layoutMode}
        onClose={() => setSettingsDialogOpen(false)}
        onSave={saveDashboardSettings}
      />

      <HeaderWidgetSettingsDialog
        open={Boolean(headerWidgetSettingsId)}
        widget={editingHeaderWidget}
        onClose={() => setHeaderWidgetSettingsId(null)}
        onSave={replaceHeaderWidget}
        dateFilterTargets={dateFilterTargetRows}
      />

      <TextWidgetFullscreenEditor
        open={Boolean(textEditorWidgetId)}
        title={(textEditorWidgetId && state.widgets[textEditorWidgetId]?.config?.title) || ''}
        body={(textEditorWidgetId && state.widgets[textEditorWidgetId]?.config?.body) || ''}
        onClose={() => setTextEditorWidgetId(null)}
        onSave={(title, body) => {
          if (!textEditorWidgetId) return;
          const cur = state.widgets[textEditorWidgetId];
          if (!cur) return;
          handleWidgetConfigSave({ ...cur.config, title, body }, textEditorWidgetId);
        }}
      />

      {editMode && Object.keys(state.widgets).length === 0 && (
        <Card
          variant="outlined"
          sx={{
            mb: 1.5,
            borderColor: 'primary.main',
            borderWidth: 2,
            bgcolor: 'background.paper',
            flexShrink: 0,
          }}
        >
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Welcome to the Dashboard Builder
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Build interactive dashboards by adding widgets, binding them to data sources, and
              arranging them on a flexible grid. Everything is saved to your browser automatically.
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              <strong>To begin, click &ldquo;Add widget&rdquo;</strong> in the toolbar above to place a map, chart, table, embed, or metric tile onto the canvas.
            </Typography>

            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Editing tips
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5, mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Configure</strong> — Click the gear icon on any widget header to open its settings (data source, columns, format rules, styling).
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Move</strong> — Drag any tile by its header bar to reposition it on the grid.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Resize</strong> — Grab the blue corner handle or any edge to resize a widget.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Stack</strong> — Hold <strong>Ctrl</strong> + drag onto the <em>center</em> of another widget to create tabs.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Group</strong> — Hold <strong>Ctrl</strong> + drag onto the <em>edge</em> of another widget to create a side-by-side group.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Duplicate &amp; delete</strong> — Use the copy and trash icons on each widget header.
              </Typography>
            </Stack>

            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Customisation
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5, mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Header appearance</strong> — Change title, subtitle, logo, background, and header filters.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Theme</strong> — Pick a colour palette, widget style, and background for the entire dashboard.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Format rules</strong> — Add per-field formatting (badges, colour maps, thresholds, images) inside widget settings.
              </Typography>
            </Stack>

            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Viewing &amp; sharing
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Remove <code style={{ fontSize: '0.85em' }}>?edit=true</code> from the URL to switch to view mode, or use the View button on the content page.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Dashboards are saved to the server automatically.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: sidePanelConfig?.enabled && sidePanelConfig.anchor === 'bottom' ? 'column' : 'row',
          bgcolor: state.theme?.dashboardBackground || undefined,
        }}
      >
        {/* Side panel — left anchor */}
        {sidePanelConfig?.enabled && sidePanelConfig.anchor === 'left' && (
          <DashboardSidePanel
            childWidgetIds={sidePanelConfig.childWidgetIds}
            anchor="left"
            panelWidth={sidePanelConfig.panelWidth}
            splitRatios={sidePanelConfig.splitRatios}
            editMode={editMode}
            theme={state.theme}
            widgets={state.widgets}
            onUpdatePanel={updateSidePanel}
            onAddChildWidget={addChildWidget}
            onRemoveChildWidget={removeChildWidget}
            onOpenChildSettings={(id) => setSettingsWidgetId(id)}
            renderWidget={renderWidgetBody}
          />
        )}

        <Box ref={gridContainerRef} sx={{ flex: 1, minHeight: 0, minWidth: 0, position: 'relative', overflow: layoutMode === 'fit' ? 'hidden' : 'auto' }}>
        <GridWithWidth
          className={`dashboard-builder-grid${editMode ? ' dashboard-builder-grid--edit' : ''}`}
          cols={12}
          rowHeight={gridRowHeight}
          margin={[GRID_MARGIN, GRID_MARGIN]}
          containerPadding={[0, 0]}
          layout={layout}
          onLayoutChange={stackTargetId || groupTargetId ? undefined : handleLayoutChange}
          onDragStart={handleDragStart}
          onDrag={handleDrag}
          onDragStop={handleDragStop}
          isDraggable={editMode}
          isResizable={editMode}
          draggableHandle={`.${DRAG_HANDLE_CLASS}`}
          resizeHandles={['se', 's', 'e']}
          compactType={ctrlDragging ? null : 'vertical'}
          preventCollision={ctrlDragging}
        >
          {layout.map((item) => {
            const instance = state.widgets[item.i];
            if (!instance) return null;
            const flushCardBody =
              instance.type === 'indicator' ||
              instance.type === 'calendar_heatmap' ||
              instance.type === 'viz_tabs' ||
              instance.type === 'chart_bar' ||
              instance.type === 'chart_line' ||
              instance.type === 'chart_donut' ||
              instance.type === 'web_map' ||
              instance.type === 'geofence' ||
              instance.type === 'device_list' ||
              instance.type === 'activity_timeline' ||
              instance.type === 'live_status' ||
              instance.type === 'widget_stack' ||
              instance.type === 'widget_group';
            return (
              <Box key={item.i} sx={{ width: '100%', minWidth: 0, height: '100%', minHeight: 0 }}>
                <Card
                  variant="outlined"
                  sx={{
                    position: 'relative',
                    width: '100%',
                    minWidth: 0,
                    height: '100%',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    ...cardStyleFromWidget(instance.config?.style, state.theme),
                  }}
                >
                  {stackTargetId === item.i && (
                    <Box
                      className="stack-drop-highlight"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 20,
                        bgcolor: 'rgba(25, 118, 210, 0.18)',
                        border: '2px dashed',
                        borderColor: 'primary.main',
                        borderRadius: 'inherit',
                        pointerEvents: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <LayersIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.7 }} />
                    </Box>
                  )}
                  {groupTargetId === item.i && (
                    <Box
                      className="group-drop-highlight"
                      sx={{
                        position: 'absolute',
                        zIndex: 20,
                        pointerEvents: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(25, 118, 210, 0.35)',
                        borderRadius: 0.5,
                        ...(groupTargetEdge === 'left' && { top: 4, bottom: 4, left: 0, width: 32 }),
                        ...(groupTargetEdge === 'right' && { top: 4, bottom: 4, right: 0, width: 32 }),
                        ...(groupTargetEdge === 'top' && { left: 4, right: 4, top: 0, height: 32 }),
                        ...(groupTargetEdge === 'bottom' && { left: 4, right: 4, bottom: 0, height: 32 }),
                        border: '2px solid',
                        borderColor: 'primary.main',
                      }}
                    >
                      <GroupIcon sx={{ fontSize: 22, color: 'primary.main' }} />
                    </Box>
                  )}
                  {(editMode || !(instance.config?.style?.hideHeader ?? state.theme?.hideHeaders ?? hideHeaders)) && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    className={DRAG_HANDLE_CLASS}
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderBottom: 1,
                      borderColor: 'divider',
                      cursor: editMode ? 'grab' : 'default',
                      flexShrink: 0,
                      '&:active': { cursor: editMode ? 'grabbing' : 'default' },
                      bgcolor: editMode ? 'action.selected' : 'background.paper',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ flex: 1, userSelect: 'none', ...titleStyleFromWidget(instance.config?.style, state.theme) }}
                    >
                      {widgetChromeTitle(instance)}
                    </Typography>
                    {instance.type === 'text' && (
                      <Tooltip title="Open full screen to read or edit">
                        <IconButton
                          size="small"
                          aria-label="Open text full screen"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTextEditorWidgetId(item.i);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <ExpandTextIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {editMode && (
                      <>
                        {instance.type === 'web_map' && (
                          <Tooltip title="Open map editor">
                            <IconButton
                              size="small"
                              aria-label="Open map editor"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMapEditorWidgetId(item.i);
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <EditMapIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Widget settings">
                          <IconButton
                            size="small"
                            aria-label="Widget settings"
                            onClick={(e) => {
                              e.stopPropagation();
                              openSettings(item.i);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate widget">
                          <IconButton
                            size="small"
                            aria-label="Duplicate widget"
                            onClick={(e) => {
                              e.stopPropagation();
                              duplicateWidget(item.i);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove widget">
                          <IconButton
                            size="small"
                            aria-label="Remove widget"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeWidget(item.i);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                  )}
                  <CardContent
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      pt: flushCardBody ? 0 : 1,
                      px: flushCardBody ? 0 : 2,
                      pb: flushCardBody ? 0 : 2,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:last-child': { pb: flushCardBody ? 0 : 2 },
                    }}
                  >
                    <ScaledWidgetContent scale={instance.config?.style?.contentScale}>
                      {renderWidgetBody(instance, item.i)}
                    </ScaledWidgetContent>
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </GridWithWidth>
        </Box>

        {/* Side panel — right anchor */}
        {sidePanelConfig?.enabled && sidePanelConfig.anchor === 'right' && (
          <DashboardSidePanel
            childWidgetIds={sidePanelConfig.childWidgetIds}
            anchor="right"
            panelWidth={sidePanelConfig.panelWidth}
            splitRatios={sidePanelConfig.splitRatios}
            editMode={editMode}
            theme={state.theme}
            widgets={state.widgets}
            onUpdatePanel={updateSidePanel}
            onAddChildWidget={addChildWidget}
            onRemoveChildWidget={removeChildWidget}
            onOpenChildSettings={(id) => setSettingsWidgetId(id)}
            renderWidget={renderWidgetBody}
          />
        )}

        {/* Side panel — bottom anchor */}
        {sidePanelConfig?.enabled && sidePanelConfig.anchor === 'bottom' && (
          <DashboardSidePanel
            childWidgetIds={sidePanelConfig.childWidgetIds}
            anchor="bottom"
            panelWidth={sidePanelConfig.panelWidth}
            splitRatios={sidePanelConfig.splitRatios}
            editMode={editMode}
            theme={state.theme}
            widgets={state.widgets}
            onUpdatePanel={updateSidePanel}
            onAddChildWidget={addChildWidget}
            onRemoveChildWidget={removeChildWidget}
            onOpenChildSettings={(id) => setSettingsWidgetId(id)}
            renderWidget={renderWidgetBody}
          />
        )}
      </Box>

      <WidgetSettingsDialog
        open={Boolean(settingsWidgetId && settingsInstance)}
        widgetType={settingsInstance?.type ?? 'placeholder'}
        initial={settingsInstance}
        onClose={() => setSettingsWidgetId(null)}
        onSave={handleWidgetConfigSave}
        allWidgets={state.widgets}
        onUnstackChild={
          settingsWidgetId && settingsInstance?.type === 'widget_stack'
            ? (childId: string) => unstackWidget(settingsWidgetId, childId)
            : undefined
        }
        onUngroupChild={
          settingsWidgetId && settingsInstance?.type === 'widget_group'
            ? (childId: string) => ungroupWidget(settingsWidgetId, childId)
            : undefined
        }
      />
    </Box>
    </DashboardThemeProvider>
    </WebMapProvider>
  );
};

export default DashboardBuilderPage;
